import { FC, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'src/app/reduxCustomHooks';
import { bootstrapAuthAction } from 'src/app/slices/authSlice';
import { exchangeSsoHandoff } from 'src/app/services/sso';
import { PageLoading } from 'src/components/atoms/PageLoading';
import LoginTemplate from 'src/components/templates/LoginTemplate';
import { sanitizeSsoReturnTo } from 'src/utils/ssoReturnTo';

/** `#h=<code>&r=<returnTo>` from the API's SSO callback redirect. */
export const readSsoFragment = (hash: string): { code: string | null; returnTo: string } => {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const code = params.get('h')?.trim() || null;
  return { code, returnTo: sanitizeSsoReturnTo(params.get('r')) };
};

/**
 * Public route /login/sso. Reads the single-use handoff code from the URL
 * fragment (never sent to a server or in a Referer), removes it from the
 * address bar at once, exchanges it for a session and continues.
 */
const SsoCallback: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const { code, returnTo } = readSsoFragment(window.location.hash);
    window.history.replaceState(window.history.state, '', window.location.pathname);
    if (!code) {
      navigate('/login?ssoError=exchangeFailed', { replace: true });
      return;
    }
    exchangeSsoHandoff(code)
      .then(({ token, user }) => {
        dispatch(bootstrapAuthAction({ token, user }));
        navigate(returnTo, { replace: true });
      })
      .catch(() => {
        navigate('/login?ssoError=exchangeFailed', { replace: true });
      });
  }, [dispatch, navigate]);

  return (
    <LoginTemplate>
      <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
        <PageLoading />
        <Typography>{t('sso.signingIn')}</Typography>
      </Stack>
    </LoginTemplate>
  );
};

export default SsoCallback;
